/**
 * Format der Versionshistorie (`custom:versionHistory`).
 *
 * Die Historie lag früher als Liste voller, unkomprimierter XML-Kopien in
 * einem XML-Attribut. Eine Datei mit sieben Ständen wog dadurch 6,3 MB, davon
 * 5,7 MB Historie — und wuchs mit jedem Speichern ungebremst weiter.
 *
 * Hier steckt jetzt das gesamte Wissen über das Format:
 *
 *   - Ein Stand wird zlib-komprimiert und base64-kodiert unter `xmlz`
 *     abgelegt. Base64 enthält kein `<`, `>`, `&` oder `"`, womit auch der
 *     Escaping-Aufschlag des XML-Attributs entfällt (er lag bei 13,8 %).
 *   - Alte Dateien tragen den Klartext unter `xml`. Beide Formen werden
 *     gelesen; geschrieben wird nur noch `xmlz`, sodass sich bestehende
 *     Dateien beim nächsten Speichern selbst verkleinern.
 *   - Nur die jüngsten MAX_SNAPSHOTS Einträge behalten ihren Rumpf. Ältere
 *     bleiben als Protokollzeile bestehen, verlieren aber Inhalt und
 *     Bildverweise — Letzteres gibt die Bilder im `imageStore` zum Aufräumen
 *     frei.
 *
 * Bewusst synchron: `sanitizeVersionHistory` prüft die Historie synchron, und
 * die Ansicht einer alten Version hängt an einem Klick-Handler. Das native
 * CompressionStream wäre abhängigkeitsfrei, aber asynchron und würde beide
 * Pfade umbauen. fflate liegt ohnehin im Bündel — jsPDF bringt es mit.
 */

import { zlibSync, unzlibSync, strToU8, strFromU8 } from 'fflate';
import { sanitizeXmlSnapshot } from './sanitize';

/** So viele Stände behalten ihren Rumpf; ältere bleiben Protokoll. */
export const MAX_SNAPSHOTS = 10;

// ─── base64 ────────────────────────────────────────────────────────

// String.fromCharCode(...bytes) sprengt bei größeren Ständen den Aufrufstapel,
// darum in Blöcken.
const CHUNK = 0x8000;

function bytesToBase64(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function base64ToBytes(b64) {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}

// ─── Packen und Entpacken ──────────────────────────────────────────

/**
 * Einen XML-Stand in die Speicherform bringen.
 * @param {string} xml
 * @returns {string} base64-kodiertes zlib
 */
export function packSnapshot(xml) {
  return bytesToBase64(zlibSync(strToU8(xml), { level: 9 }));
}

/**
 * Speicherform zurück in XML.
 * @param {string} b64
 * @returns {string|null} null, wenn der Block unlesbar ist
 */
export function unpackSnapshot(b64) {
  try {
    return strFromU8(unzlibSync(base64ToBytes(b64)));
  } catch (e) {
    console.warn('Versionsstand liess sich nicht entpacken:', e);
    return null;
  }
}

/** Trägt der Eintrag einen Stand — in welcher Form auch immer? */
export function hasSnapshot(entry) {
  return !!(entry && (entry.xmlz || entry.xml));
}

/**
 * Der rohe XML-Stand eines Eintrags, ohne Säuberung.
 * Nur für Vergleiche innerhalb dieses Moduls und für den Rücksprung gedacht.
 */
function rawSnapshot(entry) {
  if (!entry) return null;
  if (entry.xmlz) return unpackSnapshot(entry.xmlz);
  return entry.xml || null;
}

/**
 * Der XML-Stand eines Eintrags, gesäubert.
 *
 * Die Säuberung sitzt hier statt in `sanitizeVersionHistory`: dort ist der
 * Stand nur noch ein base64-Block, den man nicht ansehen kann, ohne ihn zu
 * entpacken.
 *
 * @param {object} entry
 * @returns {string|null}
 */
export function readSnapshot(entry) {
  const xml = rawSnapshot(entry);
  return xml ? (sanitizeXmlSnapshot(xml) ?? null) : null;
}

/**
 * Die Historie aus dem Attribut lesen und dabei zwischen „leer" und
 * „unlesbar" unterscheiden.
 *
 * Der Unterschied ist folgenreich: Wer eine kaputte Historie als leere Liste
 * behandelt und weiterschreibt, löscht sie beim nächsten Speichern
 * endgültig. Aufrufer, die schreiben, müssen bei `corrupt` die Finger von
 * Historie und Bildspeicher lassen.
 *
 * @param {string|null|undefined} raw
 * @returns {{history: object[], corrupt: boolean}}
 */
export function parseHistory(raw) {
  if (!raw) return { history: [], corrupt: false };
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return { history: [], corrupt: true };
    return { history: parsed, corrupt: false };
  } catch {
    return { history: [], corrupt: true };
  }
}

// ─── Einträge ──────────────────────────────────────────────────────

/** Eindeutige Kennung eines Eintrags — siehe sameEntry. */
function newId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Einen Historieneintrag bauen. Ohne `xml` entsteht eine reine
 * Protokollzeile — so verlangt es der erste Speichervorgang nach einem
 * Import, der keinen sinnvollen Vorzustand hat.
 */
export function makeEntry({ version, author, date, comment, xml, images }) {
  const entry = { id: newId(), version, author, date };
  if (comment) entry.comment = comment;
  if (xml) entry.xmlz = packSnapshot(xml);
  if (images && Object.keys(images).length > 0) entry.images = images;
  return entry;
}

/**
 * Zwei Einträge als denselben erkennen.
 *
 * Neue Einträge tragen eine Kennung, und nur die ist eindeutig. Version,
 * Datum und Stand reichen nicht: Wer eine Änderung zurücknimmt und erneut
 * speichert, erzeugt am selben Tag unter derselben Version einen Eintrag mit
 * exakt demselben Stand wie ein früherer. Ein Rücksprung auf den späteren
 * traf dann den früheren und löschte alles dazwischen mit.
 *
 * Altbestand hat keine Kennung — dort bleibt es beim alten Vergleich, samt
 * dieser Schwäche.
 */
export function sameEntry(a, b) {
  if (!a || !b) return false;
  if (a.id || b.id) return a.id === b.id;
  return a.version === b.version
    && a.date === b.date
    && (a.xmlz ?? a.xml ?? null) === (b.xmlz ?? b.xml ?? null);
}

/**
 * Einen Stand anhängen — es sei denn, er ist mit dem zuletzt gespeicherten
 * identisch. Ein Speichern ohne Änderung ist keine Version und kostete bisher
 * eine vollständige Zweitkopie.
 *
 * @returns {{history: object[], appended: boolean}}
 */
export function appendEntry(history, entry) {
  const list = Array.isArray(history) ? history : [];
  if (entry.xmlz) {
    const last = [...list].reverse().find(hasSnapshot);
    if (last && isSameSnapshot(last, entry)) {
      return { history: list, appended: false };
    }
  }
  return { history: [...list, entry], appended: true };
}

/**
 * Tragen zwei Einträge denselben Stand?
 *
 * Liegen beide komprimiert vor, genügt der Vergleich der gespeicherten Form:
 * Deflate ist für gleiche Eingabe deterministisch. Erst wenn eine Seite noch
 * Klartext ist, muss entpackt werden. Sollten zwei Blöcke einmal von
 * verschiedenen Kompressionsständen stammen, gelten sie als verschieden —
 * das kostet einen überflüssigen Eintrag, nie einen falschen Treffer.
 */
function isSameSnapshot(a, b) {
  if (a.xmlz && b.xmlz) return a.xmlz === b.xmlz;
  return rawSnapshot(a) === rawSnapshot(b);
}

/**
 * Die Historie in die Speicherform bringen: Altbestand umpacken, dann alles
 * jenseits der jüngsten `max` Stände auf Protokollzeilen eindampfen.
 *
 * Mit dem Rumpf fallen auch die Bildverweise weg. Das ist Absicht:
 * `garbageCollectImageStore` hält Bilder nur so lange, wie ein Eintrag sie
 * nennt — ein Eintrag ohne Rumpf kann sie ohnehin nicht mehr herstellen.
 */
export function normalizeHistory(history, max = MAX_SNAPSHOTS) {
  const list = Array.isArray(history) ? history : [];

  const packed = list.map((raw) => {
    if (!raw || typeof raw !== 'object') return raw;
    // Altbestand nachträglich kennzeichnen, sonst bleibt er für immer auf den
    // mehrdeutigen Vergleich in sameEntry angewiesen.
    const entry = raw.id ? raw : { id: newId(), ...raw };
    // Trägt ein Eintrag beides, gilt die komprimierte Form — dieselbe
    // Rangfolge wie beim Lesen. Der übrig gebliebene Klartext fliegt raus.
    if (entry.xmlz) {
      const { xml, ...rest } = entry;
      return rest;
    }
    if (!entry.xml) return entry;
    // Altbestand: Klartext in die komprimierte Form heben.
    const { xml, ...rest } = entry;
    return { ...rest, xmlz: packSnapshot(xml) };
  });

  let kept = 0;
  const trimmed = [];
  for (let i = packed.length - 1; i >= 0; i--) {
    const entry = packed[i];
    if (hasSnapshot(entry)) {
      if (kept < max) {
        kept++;
        trimmed.unshift(entry);
        continue;
      }
      const { xmlz, xml, images, ...rest } = entry;
      trimmed.unshift(rest);
      continue;
    }
    trimmed.unshift(entry);
  }
  return trimmed;
}
